<script setup>
import { onMounted, computed, watch, ref } from "vue";
import { useRouter } from "vue-router";
import moment from "moment";

import Filters from "@/components/company/filters/Filters.vue";

import debounce from "lodash/debounce";
import { useCompanyStore } from "@/stores";

const companyStore = useCompanyStore();

const router = useRouter();

const data = computed(() => companyStore.overview);

const params = computed(() => companyStore.requestQueryParams);

const pagination = computed(() => companyStore.pagination);

const isFetching = ref(false);

const formatDateTime = (date) => {
  if (date === null) {
    return date;
  }
  return moment(date, "YYYY-MM-DDTHH:mm:ss").format("DD-MM-YYYY HH:mm");
};

const getCompanies = async () => {
  isFetching.value = true;

  params.value.set("page", pagination.value.currentPage - 1);
  params.value.set("size", pagination.value.pageSize);

  await companyStore.getCompanyOverview(params.value);
  isFetching.value = false;
};

const checkClickEvent = (event) => {
  const check = event.target && event.target.classList.contains("mdi-pencil");
  return check;
};

const edit = (item, event) => {
  const check = checkClickEvent(event);
  if (check) {
    router.push({
      path: `/company/edit/${item}`,
    });
  }
};

const download = ref(false);

const exportTable = async () => {
  download.value = true;
  const resp = await companyStore.downloadExcel(params.value);

  const url = window.URL.createObjectURL(resp.data);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "companies.xlsx");
  document.body.appendChild(link);
  link.click();
  link.remove();
  download.value = false;
};

const companiesDebounced = debounce(getCompanies, 500);

watch(
  pagination,
  () => {
    if (!isFetching.value) {
      getCompanies();
    }
  },
  {
    deep: true,
  }
);

watch(
  params,
  () => {
    companiesDebounced();
  },
  {
    deep: true,
  }
);

const headers = [
  {
    title: "Actions",
    key: "actions",
    align: "left",
    sortable: false,
    fixed: true,
  },
  { title: "Name", key: "name", width: "300px" },
  { title: "Created", key: "createdAt", width: "300px" },
  { title: "Contact Person", key: "contactName", width: "200px" },
  { title: "Telephone", key: "telephone" },
  { title: "Email", key: "email" },
  { title: "Job Positions", key: "jobPositions" },
  { title: "Odyssea Branch", key: "branch" },
  { title: "Email (Contact Person)", key: "contactEmail" },
  { title: "Communication Responsible", key: "responsibleUser" },
  { title: "Company Sector", key: "companySector" },
  { title: "Logged by", key: "loggedBy" },
];

// onBeforeMount(() => {
//   companyStore.resetAction();
// });

onMounted(() => {
  getCompanies();
});
</script>

<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h1 class="mb-10 page-title">Companies Overview</h1>
        <Filters variant="company" />
        <div class="d-flex justify-space-between align-center mb-5">
          <h3>Total: {{ pagination.totalItems }}</h3>
          <v-btn
            color="company"
            :loading="download"
            :disabled="download"
            @click="exportTable()"
          >
            <div class="d-flex align-center">
              <v-icon>mdi-file-excel</v-icon>
              <span>Export</span>
            </div>
          </v-btn>
        </div>
        <v-data-table
          :items="data"
          :headers="headers"
          :items-per-page="pagination.pageSize"
          :hide-default-footer="true"
          :loading="isFetching"
          class="company-table bordered-columns"
        >
          <template #item.name="{ item }">
            <router-link
              :to="'/company/view/' + item.id"
              class="chip-link-color-company"
            >
              <v-chip> {{ item.name }} </v-chip>
            </router-link>
          </template>

          <template #item.createdAt="{ item }">
            <span style="white-space: nowrap">{{
              formatDateTime(item.createdAt)
            }}</span>
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex ga-2 justify-center">
              <v-btn
                icon="mdi-pencil"
                size="x-small"
                color="company"
                @click="edit(item.id, $event)"
              />
            </div>
          </template>
        </v-data-table>
        <div class="mt-5">
          <v-pagination
            v-model="pagination.currentPage"
            :length="pagination.pageCount"
            :total-visible="5"
          />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
h1 {
  color: rgb(var(--v-theme-company));
}
.v-table {
  table-layout: fixed;

  td,
  th {
    border-right: thin solid
      rgba(var(--v-border-color), var(--v-border-opacity));
    white-space: normal;
  }
}
</style>
